import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface VideoCarouselProps {
  videos: string[]
  duration?: number // Duration in seconds for each video
}

export const VideoCarousel = ({ videos, duration = 10 }: VideoCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [, setIsLoaded] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % videos.length)
    }, duration * 1000)

    return () => clearInterval(timer)
  }, [videos.length, duration])

  useEffect(() => {
    // Preload first video
    const video = document.createElement('video')
    video.src = videos[0]
    video.onloadeddata = () => setIsLoaded(true)
  }, [videos])

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  const toggleMute = () => {
    setIsMuted((prev) => !prev)
    // После клика пользовательского ввода браузер позволяет воспроизводить со звуком
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      videoRef.current
        .play()
        .catch(() => {
          /* ignore */
        })
    }
  }

  return (
    <div className="absolute inset-0 z-0">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <video
            ref={videoRef}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            style={{ objectFit: 'cover', imageRendering: 'crisp-edges' }}
            key={videos[currentIndex]}
          >
            <source src={videos[currentIndex]} type="video/mp4" />
          </video>
        </motion.div>
      </AnimatePresence>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80 z-10" />

      {/* Navigation Dots */}
      {videos.length > 1 && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
          {videos.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`group relative transition-all duration-300 ${
                index === currentIndex ? 'w-12' : 'w-3'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            >
              {/* Background bar */}
              <div
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-white/30'
                    : 'bg-white/20 group-hover:bg-white/30'
                }`}
              />
              
              {/* Progress bar (only for active slide) */}
              {index === currentIndex && (
                <motion.div
                  className="absolute top-0 left-0 h-1 bg-white rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: duration, ease: 'linear' }}
                  key={`progress-${currentIndex}`}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Video Counter */}
      <div className="absolute top-8 right-8 z-20 flex items-center gap-4 text-white/60 text-sm font-mono">
        <span>
          {String(currentIndex + 1).padStart(2, '0')} / {String(videos.length).padStart(2, '0')}
        </span>

        {/* Mute / Unmute button */}
        <button
          type="button"
          onClick={toggleMute}
          className="px-3 py-1 rounded-full bg-black/40 border border-white/30 text-xs uppercase tracking-wide hover:bg-black/60 transition-colors"
        >
          {isMuted ? 'Unmute' : 'Mute'}
        </button>
      </div>
    </div>
  )
}
